/**
 * ADR-129 §6 — SMS-specific swap effect for the localized-frame contract.
 *
 * The app-agnostic contract core — localized-frame detection, staleness-key
 * timestamp parsing, translated-field extraction, and the T_ws accept-window
 * tracker — lives in `@tiaki/alert-localization`, shared with AIM.
 * This module keeps only what is SMS-shaped:
 *
 *   1. Render-first — SMS renders alerts by invalidating React Query keys on
 *      `AlertHistory` frames and refetching; that path is untouched and never
 *      waits on localization (the operational-language floor, ADR-129 §1).
 *   2. Swap — when the localized frame arrives, patch the translated field
 *      into the already-fetched alert rows in the React Query cache, matched
 *      by alert id. Localized frames NEVER flow through the normal handler
 *      registry, so a translation can never replay handler side effects.
 *   3. Failover — if no localized frame arrives within T_ws, the operational
 *      English text is simply kept (nothing to undo).
 *   4. Stale-drop — ignore a localized frame older than the latest
 *      operational frame for that alert_id (keyed on the frame timestamp,
 *      since translation_version is only per-worker monotonic).
 *
 * For Alert frames the single server-translated field is the nested
 * alert-type display name `data.type.name` (see tiaki
 * `translate_websocket_payload`). SMS renders that string as
 * `alert_type_name` on `/api/v2/alert-history-detailed/` rows (Events page
 * list + alert detail drawer), so the swap patches `alert_type_name` on
 * cached rows belonging to the localized alert. AlertHistory rows link to
 * their Alert via `original_alert_id` (set to the Alert pk at creation) with
 * the `text` prefix ("<alert_id>: <name>") as fallback — both are serialized
 * by `AlertHistoryDetailSerializer`.
 *
 * @see doc/adr/decisions/129-real-time-translation-delivery.md §6
 * @see libs/alert-localization/src/lib/alert-localization.ts
 */

import type { QueryClient } from '@tanstack/react-query';
import {
  type AlertLocalizationTracker,
  extractLocalizedAlertTypeName,
  isLocalizedRebroadcast,
  parseFrameTimestamp,
} from '@tiaki/alert-localization';
import { alertHistoryKeys } from '../../hooks/queries/useAlerts';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Whether an `alert-history-detailed` row belongs to Alert `alertId`.
 * `original_alert_id` is set to the Alert pk when the row is created
 * (schools/signals/alerts.py); the `text` prefix ("<alert_id>: <name>",
 * cf. `AlertHistory.get_alert_id_from_text`) is the fallback.
 */
function rowMatchesAlert(
  row: Record<string, unknown>,
  alertId: number,
): boolean {
  const originalAlertId = row['original_alert_id'];
  if (typeof originalAlertId === 'number' && originalAlertId === alertId) {
    return true;
  }
  const text = row['text'];
  if (typeof text === 'string' && text.length > 0) {
    const prefix = Number.parseInt(text.split(':')[0] ?? '', 10);
    if (Number.isFinite(prefix) && prefix === alertId) return true;
  }
  return false;
}

function patchRow(
  row: unknown,
  alertId: number,
  translatedName: string,
): { row: unknown; changed: boolean } {
  if (!isRecord(row) || !rowMatchesAlert(row, alertId)) {
    return { row, changed: false };
  }
  if (row['alert_type_name'] === translatedName) return { row, changed: false };
  return { row: { ...row, alert_type_name: translatedName }, changed: true };
}

/**
 * Updater for one cached `alertHistory` entry. Handles both the paginated
 * list shape (`{ results: [...] }`) and the single-row detail shape.
 * Returns `undefined` when nothing matched so TanStack Query leaves the
 * cache entry untouched (no spurious re-renders).
 */
function patchCacheEntry(
  current: unknown,
  alertId: number,
  translatedName: string,
): unknown {
  if (!isRecord(current)) return undefined;

  const results = current['results'];
  if (Array.isArray(results)) {
    let changed = false;
    const next = results.map((row) => {
      const patched = patchRow(row, alertId, translatedName);
      changed = changed || patched.changed;
      return patched.row;
    });
    return changed ? { ...current, results: next } : undefined;
  }

  const patched = patchRow(current, alertId, translatedName);
  return patched.changed ? patched.row : undefined;
}

/**
 * The SMS "swap": patch the translated alert-type name onto every cached
 * `alertHistory` row (list pages + detail entries) belonging to `alertId`.
 * Purely additive — a later refetch restores server truth (the operational
 * English floor), which is the correct failover per ADR-129 §1.
 */
export function applyLocalizedAlertTypeName(
  queryClient: QueryClient,
  alertId: number,
  translatedName: string,
): void {
  if (!Number.isFinite(alertId) || alertId <= 0) return;
  queryClient.setQueriesData<unknown>(
    { queryKey: alertHistoryKeys.all },
    (current: unknown) => patchCacheEntry(current, alertId, translatedName),
  );
}

export interface ParsedSocketFrame {
  type: string;
  data: Record<string, unknown>;
  raw: Record<string, unknown>;
}

/**
 * Single router entry point for the ADR-129 §6 contract. Call BEFORE the
 * reliable-broadcast id-dedupe and handler dispatch.
 *
 * Returns `true` when the frame was a localized re-broadcast and has been
 * fully consumed (swap applied, or dropped as stale / past-T_ws / not an
 * Alert) — the router must return without dispatching it. Localized frames
 * must never reach the handler registry: replaying them would re-trigger
 * invalidation side effects for a frame that changes nothing operationally.
 *
 * Returns `false` for every other frame (including the operational Alert
 * frame, which arms the accept window here and then flows on unchanged).
 */
export function processAlertLocalizationFrame(
  parsed: ParsedSocketFrame,
  tracker: AlertLocalizationTracker,
  queryClient: QueryClient,
): boolean {
  const { type, data, raw } = parsed;

  if (isLocalizedRebroadcast(raw)) {
    if (type === 'Alert' && typeof raw['alert_id'] === 'number') {
      const alertId = raw['alert_id'];
      if (tracker.shouldApply(alertId, parseFrameTimestamp(raw))) {
        const translatedName = extractLocalizedAlertTypeName(data);
        if (translatedName) {
          applyLocalizedAlertTypeName(queryClient, alertId, translatedName);
        }
        tracker.markApplied(alertId);
      }
    }
    // AlertType localized re-broadcasts (no alert_id), stale Alert frames,
    // and frames arriving after T_ws are intentionally swallowed here.
    return true;
  }

  // Operational Alert frame: arm/refresh the T_ws accept window for its id so
  // a following localized frame can be matched and stale-dropped against it.
  // SMS has no Alert handler today, so the frame otherwise flows on to the
  // registry as an ignored type — behavior unchanged.
  if (type === 'Alert') {
    const id = data['id'];
    if (typeof id === 'number' && Number.isFinite(id)) {
      tracker.recordOperational(id, parseFrameTimestamp(raw));
    }
  }
  return false;
}
